import React, { useState, useEffect, useCallback } from 'react';
import { getDietOrders } from '../api/dietitianApi';
import { Pagination } from '../components/Pagination';
import type { DietOrderListItem, DietStats, DietOrderStatus } from '../types/dietitian.types';

interface OrdersPageProps {
  onSelectOrder: (id: number) => void;
}

const STATUS_BADGE: Record<DietOrderStatus, { label: string; color: string; bg: string }> = {
  'wc-diet-pending':   { label: 'Nowe',     color: '#92400E', bg: '#FEF3C7' },
  'wc-diet-confirmed': { label: 'Dziś',     color: '#1E40AF', bg: '#DBEAFE' },
  'wc-diet-done':      { label: 'Gotowe ✓', color: '#166534', bg: '#DCFCE7' },
  'wc-cancelled':      { label: 'Anulowane', color: '#991B1B', bg: '#FEE2E2' },
  'cancelled':         { label: 'Anulowane', color: '#991B1B', bg: '#FEE2E2' },
};

const TYPE_ICON: Record<string, string> = {
  jadlospis:   '🥗',
  konsultacja: '💬',
  pakiet:      '📦',
};

type Tab = 'todo' | 'inprogress' | 'done';

export const OrdersPage: React.FC<OrdersPageProps> = ({ onSelectOrder }) => {
  const [tab, setTab]         = useState<Tab>('todo');
  const [orders, setOrders]   = useState<DietOrderListItem[]>([]);
  const [stats, setStats]     = useState<DietStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  useEffect(() => { setCurrentPage(1); }, [tab, search]);

  const user = (window as any).dietitianVars?.initialUser || (window as any).cabinetVars?.initialUser;
  const userName = user?.first_name 
    ? user.first_name + ' ' + (user.last_name ? user.last_name.charAt(0) + '.' : '') 
    : (user?.display_name || 'Dietetyk');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDietOrders(tab);
      setOrders(data.orders);
      setStats(data.stats);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  // Polling co 30 sek
  useEffect(() => {
    const iv = setInterval(load, 30_000);
    return () => clearInterval(iv);
  }, [load]);

  const filtered = orders.filter(o =>
    !search || o.customer_name.toLowerCase().includes(search.toLowerCase()) ||
    o.customer_email.toLowerCase().includes(search.toLowerCase())
  );

  const paginatedOrders = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div>
      <div className="orders-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1>Dzień dobry, {userName.split(' ')[0]} 👋</h1>
          <div className="muted">Oto Twoje dzisiejsze zlecenia.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            style={{ border: '1px solid var(--lav)', borderRadius: 12, padding: '0 16px', height: 38, outline: 'none', fontFamily: 'inherit' }}
            type="text"
            placeholder="Szukaj pacjentki..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button className="btn ghost sm" onClick={load} title="Odśwież" style={{ padding: '0 12px', height: 38, fontSize: 16 }}>↺</button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="statchips">
          <div className="sc"><b>{stats.consultations_today}</b> konsultacje dziś</div>
          <div className="sc"><b>{stats.jadlospis_pending}</b> jadłospisy do przygotowania</div>
          <div className="sc"><b>{stats.total_todo}</b> łącznie do zrobienia</div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        {(['todo', 'inprogress', 'done'] as Tab[]).map(t => (
          <div
            key={t}
            className={`tab ${tab === t ? 'on' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'todo' ? `Do zrobienia (${stats?.total_todo || 0})` : t === 'inprogress' ? `W toku (${stats?.total_todo || 0})` : `Gotowe (${stats?.total_done || 0})`}
          </div>
        ))}
      </div>

      {/* Order list */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[1,2,3].map(i => <div key={i} style={{ height: 72, background: 'linear-gradient(90deg, #f0ebfc, #e8e0f8)', borderRadius: 16, animation: 'pulse 1.5s ease-in-out infinite' }} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--v600)' }}>
          <span style={{ fontSize: 48 }}>🎉</span>
          <p>Brak zamówień w tej kategorii</p>
        </div>
      ) : (
        <div>
          {paginatedOrders.map(order => (
            <OrderRow key={order.id} order={order} onClick={() => onSelectOrder(order.id)} />
          ))}
          
          <div style={{ marginTop: 24 }}>
            <Pagination
              totalItems={filtered.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const OrderRow: React.FC<{ order: DietOrderListItem; onClick: () => void }> = ({ order, onClick }) => {
  const initials = order.customer_name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  let badgeLabel = 'Nowe';
  let badgeClass = 'b-new';
  
  const isDone = order.status === 'wc-diet-done' || order.has_pdf || order.has_zalecenia;
  
  if (isDone) {
    badgeClass = 'b-done';
    badgeLabel = 'Gotowe · wysłane';
  } else if (order.slot_confirmed || order.slot) {
    badgeClass = 'b-today';
    const activeSlot = (order.slot_confirmed || order.slot || '');
    const parts = activeSlot.split(' ');
    if (parts.length >= 2) {
      const d = parts[0].split('-');
      const t = parts[1].slice(0, 5);
      const tzOffset = (new Date()).getTimezoneOffset() * 60000;
      const todayStr = (new Date(Date.now() - tzOffset)).toISOString().split('T')[0];
      badgeLabel = parts[0] === todayStr ? `Dziś ${t}` : `${d[2]}.${d[1]} ${t}`;
    } else {
      badgeLabel = activeSlot;
    }
  } else if (order.has_admin_note || order.type === 'pakiet') {
    badgeClass = order.type === 'pakiet' ? 'b-new' : 'b-prep';
    badgeLabel = order.type === 'pakiet' ? 'Wpisz zalecenia + menu' : 'W przygotowaniu';
  }

  const shortDate = order.ordered_at ? order.ordered_at.split(' ')[0].slice(0, 5) : '';
  let subText = `zamówione ${shortDate}`;
  if (order.type === 'konsultacja') {
    subText = 'konsultacja';
  } else if (order.type === 'pakiet' && order.status !== 'wc-diet-pending') {
    subText = 'po konsultacji';
  } else if (isDone) {
    subText = `wysłane ${shortDate}`;
  }

  const showMeetButton = order.meet_link && (order.type === 'konsultacja' || order.type === 'pakiet') && !isDone;

  return (
    <div className="row" onClick={onClick} style={{ cursor: 'pointer' }}>
      <div className="av">{initials || '?'}</div>

      <div className="who">
        <div className="nm">{order.customer_name}</div>
        <div className="sub">{subText}</div>
      </div>

      <span className="ttag">{order.type_label}</span>

      <span className={`badge ${badgeClass}`}>{badgeLabel}</span>

      <div className="spacer"></div>

      {showMeetButton && (
        <button
          className="btn sm meet"
          onClick={(e) => { e.stopPropagation(); window.open(order.meet_link!, '_blank'); }}
        >
          Dołącz do Meet
        </button>
      )}

      <button 
        className={isDone || showMeetButton ? 'btn sm ghost' : 'btn sm'}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
      >
        {isDone ? 'Podgląd' : (showMeetButton ? 'Otwórz' : 'Otwórz →')}
      </button>
    </div>
  );
};
